// Pasar una cartera de un archivo al programa.
//
// Lo que rompe estos importadores no es leer el archivo: es que aquí un millón
// y medio se escribe «1.500.000» y una fecha se escribe «15/07/2026». Un
// importador hecho pensando en inglés lee 1,5 y el 7 de julio, y mete al
// cliente con un préstamo de un peso con cinco. Por eso los números y las
// fechas van primero y con pruebas propias.
//
// Nada de esto guarda nada: devuelve filas para revisar. Quien importa ve la
// tabla, corrige lo que esté mal y solo entonces se guarda.

// ── El separador ────────────────────────────────────────────────────────────
//
// En Colombia el Excel guarda los CSV con PUNTO Y COMA, porque la coma es el
// separador decimal. Un importador que asuma la coma parte «1.500.000» en dos
// columnas. Se decide contando en la primera línea, que es lo único que no
// falla: gana el que aparezca más veces.
export function separadorDe(texto) {
  const primera = String(texto || '').split(/\r?\n/)[0] || '';
  const cuenta = (c) => (primera.split(c).length - 1);
  const candidatos = [[';', cuenta(';')], ['\t', cuenta('\t')], [',', cuenta(',')]];
  candidatos.sort((a, b) => b[1] - a[1]);
  return candidatos[0][1] > 0 ? candidatos[0][0] : ';';
}

// Un CSV de verdad: campos entre comillas que pueden llevar el separador
// dentro, y comillas dobladas para escribir una comilla. Partir por el
// separador a secas descuadra cualquier archivo con una dirección dentro.
export function leerCSV(texto) {
  const sep = separadorDe(texto);
  const filas = [];
  let campo = '';
  let fila = [];
  let entreComillas = false;
  const cadena = String(texto || '').replace(/^﻿/, '');   // marca de Excel

  for (let i = 0; i < cadena.length; i++) {
    const c = cadena[i];
    if (entreComillas) {
      if (c === '"') {
        if (cadena[i + 1] === '"') { campo += '"'; i++; }
        else entreComillas = false;
      } else campo += c;
      continue;
    }
    if (c === '"') { entreComillas = true; continue; }
    if (c === sep) { fila.push(campo); campo = ''; continue; }
    if (c === '\n') { fila.push(campo); filas.push(fila); fila = []; campo = ''; continue; }
    if (c === '\r') continue;
    campo += c;
  }
  if (campo !== '' || fila.length) { fila.push(campo); filas.push(fila); }

  // Las líneas vacías del final se descartan: Excel las deja siempre.
  return filas.filter(f => f.some(x => String(x).trim() !== ''));
}

// ── Los números ─────────────────────────────────────────────────────────────
//
// «$ 1.500.000», «1.500.000», «1500000», «1,500,000», «1500000,50». La regla
// que resuelve todos: manda el ÚLTIMO separador que aparezca, y solo es decimal
// si le siguen una o dos cifras. Con tres detrás son miles —así se escribe el
// dinero aquí— y con más de uno, también.
export function numeroDeAqui(valor) {
  if (typeof valor === 'number') return Number.isFinite(valor) ? valor : null;
  const t = String(valor == null ? '' : valor).trim();
  if (!t) return null;

  const negativo = /^\(.*\)$/.test(t) || t.startsWith('-');
  const limpio = t.replace(/[^\d.,]/g, '');
  if (!limpio) return null;

  const ultimoPunto = limpio.lastIndexOf('.');
  const ultimaComa = limpio.lastIndexOf(',');
  const corte = Math.max(ultimoPunto, ultimaComa);

  let entero, decimales = '';
  if (corte === -1) {
    entero = limpio;
  } else {
    const detras = limpio.length - corte - 1;
    if (detras >= 1 && detras <= 2) {
      entero = limpio.slice(0, corte);
      decimales = limpio.slice(corte + 1);
    } else {
      entero = limpio;   // todos son separadores de miles
    }
  }
  const n = Number(entero.replace(/[.,]/g, '') + (decimales ? '.' + decimales : ''));
  if (!Number.isFinite(n)) return null;
  return negativo ? -n : n;
}

// ── Las fechas ──────────────────────────────────────────────────────────────
//
// Aquí se escribe DÍA/MES/AÑO. Un importador que lea 07/08/2026 como el 8 de
// julio le corre el corte a un cliente un mes entero.
//
// Se devuelve como texto AAAA-MM-DD y no como Date: una fecha suelta no tiene
// hora, y convertirla a Date la ata a una zona horaria. Eso es exactamente lo
// que corría los días en el resto del programa.
export function fechaDeAqui(valor) {
  if (valor instanceof Date && !isNaN(valor.getTime())) {
    return valor.toISOString().slice(0, 10);
  }
  const t = String(valor == null ? '' : valor).trim();
  if (!t) return null;

  // Ya viene en el formato universal
  const yaEsta = t.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (yaEsta) return armar(+yaEsta[1], +yaEsta[2], +yaEsta[3]);

  const partes = t.match(/^(\d{1,2})[/\-. ](\d{1,2})[/\-. ](\d{2,4})/);
  if (partes) {
    let [, d, m, a] = partes.map(Number);
    if (a < 100) a += a < 70 ? 2000 : 1900;
    // Si el primero no puede ser día pero el segundo sí, venía al revés
    if (d > 12 && m <= 12) return armar(a, m, d);
    if (m > 12 && d <= 12) return armar(a, d, m);
    return armar(a, m, d);
  }

  // Excel guarda las fechas como número de días desde el 30/12/1899
  const n = numeroDeAqui(t);
  if (n != null && n > 20000 && n < 80000) {
    const base = Date.UTC(1899, 11, 30);
    return new Date(base + Math.round(n) * 86400000).toISOString().slice(0, 10);
  }
  return null;
}

const armar = (a, m, d) => {
  if (!(a >= 1900 && a <= 2200) || !(m >= 1 && m <= 12) || !(d >= 1 && d <= 31)) return null;
  const f = new Date(Date.UTC(a, m - 1, d));
  if (f.getUTCMonth() !== m - 1 || f.getUTCDate() !== d) return null;  // 31 de febrero
  return `${a}-${String(m).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
};

// ── Adivinar qué es cada columna ────────────────────────────────────────────
//
// Nadie va a tener los encabezados que a uno se le ocurran. Se reconocen los
// nombres que la gente usa de verdad, sin tildes y sin importar mayúsculas, y
// lo que no se reconozca queda para que lo elija a mano.
const SIN_TILDES = (t) => String(t || '').toLowerCase()
  .normalize('NFD').replace(/[̀-ͯ]/g, '').trim();

const NOMBRES = {
  name: ['nombre', 'cliente', 'nombres', 'nombre completo', 'deudor', 'apellidos y nombres',
         'nombre y apellido', 'nombre del cliente', 'razon social'],
  cedula: ['cedula', 'cc', 'c.c', 'documento', 'identificacion', 'nit', 'numero de documento',
           'no documento', 'doc'],
  phone: ['telefono', 'celular', 'tel', 'movil', 'whatsapp', 'numero', 'contacto'],
  address: ['direccion', 'domicilio', 'barrio', 'ubicacion'],
  amount: ['monto', 'valor', 'prestamo', 'capital', 'valor prestado', 'monto prestado',
           'credito', 'deuda', 'valor credito', 'presto'],
  interest_rate: ['interes', 'tasa', 'porcentaje', 'interes %', 'tasa de interes', '%'],
  start_date: ['fecha', 'fecha inicio', 'fecha de inicio', 'inicio', 'fecha prestamo',
               'fecha del prestamo', 'desde'],
  maturity_date: ['vencimiento', 'fecha vencimiento', 'fecha de vencimiento', 'vence',
                  'fecha fin', 'hasta'],
};

export function adivinarColumnas(encabezados = []) {
  const mapa = {};
  const usadas = new Set();

  // Primero los que calzan exacto; después los que solo se parecen. Si no,
  // «fecha vencimiento» se llevaría la columna de «fecha».
  for (const exacto of [true, false]) {
    for (const [campo, alias] of Object.entries(NOMBRES)) {
      if (mapa[campo] !== undefined) continue;
      for (let i = 0; i < encabezados.length; i++) {
        if (usadas.has(i)) continue;
        const h = SIN_TILDES(encabezados[i]);
        if (!h) continue;
        const calza = exacto
          ? alias.includes(h)
          : alias.some(a => a.length >= 4 && h.includes(a));
        if (calza) { mapa[campo] = i; usadas.add(i); break; }
      }
    }
  }
  return mapa;
}

// ── Revisar antes de guardar ────────────────────────────────────────────────
//
// Cada fila vuelve con lo que se entendió y con sus problemas. Los problemas se
// separan en dos: los que impiden guardar la fila y los avisos. Mezclarlos hace
// que quien importa no sepa qué tiene que arreglar de verdad.
export function revisarFilas(filas, mapa, opciones = {}) {
  const tasaPorDefecto = Number(opciones.interesPorDefecto);
  const cedulasVistas = new Map();

  return filas.map((fila, i) => {
    const lee = (campo) => (mapa[campo] === undefined ? '' : (fila[mapa[campo]] ?? ''));
    const errores = [];
    const avisos = [];

    const name = String(lee('name') || '').trim();
    if (!name) errores.push('sin nombre');

    const cedula = String(lee('cedula') || '').replace(/[^\dA-Za-z-]/g, '').trim();
    if (!cedula) errores.push('sin cédula');
    else if (cedulasVistas.has(cedula)) {
      errores.push(`cédula repetida (fila ${cedulasVistas.get(cedula) + 1})`);
    } else cedulasVistas.set(cedula, i);

    // El teléfono se deja pasar aunque falte: sin él no hay WhatsApp, pero el
    // cliente sí existe. Se avisa, no se bloquea.
    const phone = String(lee('phone') || '').replace(/[^\d]/g, '');
    if (!phone) avisos.push('sin teléfono: no le podrá mandar el estado de cuenta');
    else if (phone.length < 7) avisos.push(`teléfono raro (${phone})`);

    // La casilla vacía y la casilla con basura son cosas distintas. Vacía
    // significa que ese cliente no tiene préstamo —muy común: media cartera
    // son clientes viejos que ya pagaron—; con algo escrito que no es un
    // número, algo se tecleó mal y hay que mirarlo.
    const montoEscrito = String(lee('amount') || '').trim();
    const amount = montoEscrito ? numeroDeAqui(montoEscrito) : null;
    if (montoEscrito) {
      if (amount == null) errores.push('el monto no se entiende');
      else if (amount <= 0) errores.push('el monto es cero o negativo');
    }

    // El interés solo tiene sentido si hay préstamo. Ponerlo en una fila sin
    // monto hace creer, mirando la tabla, que ahí va a entrar un préstamo.
    let interest_rate = amount ? numeroDeAqui(lee('interest_rate')) : null;
    if (amount && interest_rate == null && Number.isFinite(tasaPorDefecto)) {
      interest_rate = tasaPorDefecto;
      avisos.push(`sin interés: se usa el suyo, ${tasaPorDefecto}%`);
    }
    if (interest_rate != null && (interest_rate < 0 || interest_rate > 100)) {
      errores.push(`interés fuera de rango (${interest_rate})`);
    }

    const start_date = fechaDeAqui(lee('start_date'));
    if (mapa.start_date !== undefined && lee('start_date') && !start_date) {
      errores.push('la fecha no se entiende');
    }
    const maturity_date = fechaDeAqui(lee('maturity_date'));
    if (start_date && maturity_date && maturity_date <= start_date) {
      errores.push('el vencimiento es anterior al inicio');
    }

    return {
      fila: i,
      datos: {
        name, cedula, phone,
        address: String(lee('address') || '').trim() || null,
        amount, interest_rate,
        start_date: start_date || null,
        maturity_date: maturity_date || null,
      },
      // Con monto se crea también el préstamo; sin monto, solo el cliente.
      conPrestamo: amount != null && amount > 0,
      errores,
      avisos,
    };
  });
}

// ── Leer un .xlsx sin librerías ─────────────────────────────────────────────
//
// La competencia lo hace metiendo una librería entera. Aquí no hace falta: un
// .xlsx por dentro es un ZIP con archivos XML, y el navegador ya trae con qué
// descomprimir (DecompressionStream) y con qué leer XML (DOMParser). Cero
// dependencias nuevas que vigilar, y el cliente suelta su archivo y ya.
//
// Las fechas no se resuelven mirando los estilos —eso obligaría a leer otro
// XML más—: Excel las guarda como número de días, y `fechaDeAqui` ya sabe
// convertir ese número. Solo se aplica en las columnas de fecha, así que un
// monto de 46.218 pesos no se convierte en una fecha por error.

const LEE16 = (v, i) => v.getUint16(i, true);
const LEE32 = (v, i) => v.getUint32(i, true);

// Los archivos que hay dentro del ZIP, por nombre.
function abrirZip(buffer) {
  const bytes = new Uint8Array(buffer);
  const v = new DataView(buffer);

  // El índice del ZIP va al FINAL del archivo, y hay que buscarlo hacia atrás:
  // puede llevar un comentario detrás de longitud desconocida.
  let fin = -1;
  for (let i = bytes.length - 22; i >= 0 && i >= bytes.length - 66000; i--) {
    if (LEE32(v, i) === 0x06054b50) { fin = i; break; }
  }
  if (fin < 0) throw new Error('no parece un archivo de Excel');

  const cuantos = LEE16(v, fin + 10);
  let p = LEE32(v, fin + 16);
  const entradas = new Map();

  for (let n = 0; n < cuantos; n++) {
    if (LEE32(v, p) !== 0x02014b50) break;
    const metodo = LEE16(v, p + 10);
    const comprimido = LEE32(v, p + 20);
    const largoNombre = LEE16(v, p + 28);
    const largoExtra = LEE16(v, p + 30);
    const largoNota = LEE16(v, p + 32);
    const dondeEmpieza = LEE32(v, p + 42);
    const nombre = new TextDecoder().decode(bytes.subarray(p + 46, p + 46 + largoNombre));

    // La cabecera local repite el nombre y los extras, y sus longitudes NO
    // tienen por qué coincidir con las del índice. Hay que leerlas de ahí.
    const ln = LEE16(v, dondeEmpieza + 26);
    const le = LEE16(v, dondeEmpieza + 28);
    const datos = dondeEmpieza + 30 + ln + le;

    entradas.set(nombre, { metodo, datos, comprimido });
    p += 46 + largoNombre + largoExtra + largoNota;
  }
  return { bytes, entradas };
}

async function sacarTexto(zip, nombre) {
  const e = zip.entradas.get(nombre);
  if (!e) return null;
  const trozo = zip.bytes.subarray(e.datos, e.datos + e.comprimido);
  if (e.metodo === 0) return new TextDecoder().decode(trozo);
  if (e.metodo !== 8) throw new Error('el archivo usa una compresión que no se entiende');
  const flujo = new Blob([trozo]).stream()
    .pipeThrough(new DecompressionStream('deflate-raw'));
  return await new Response(flujo).text();
}

// «A» es 0, «B» es 1, «AA» es 26. Hace falta para respetar las columnas
// vacías: Excel no las escribe, y sin esto los datos se corren a la izquierda.
export function columnaDeCelda(ref) {
  const letras = String(ref || '').match(/^[A-Z]+/);
  if (!letras) return -1;
  let n = 0;
  for (const c of letras[0]) n = n * 26 + (c.charCodeAt(0) - 64);
  return n - 1;
}

export async function leerXLSX(buffer) {
  const zip = abrirZip(buffer);
  const dom = new DOMParser();

  // El texto se guarda una sola vez y las celdas apuntan a él por número.
  const compartidas = [];
  const xmlTextos = await sacarTexto(zip, 'xl/sharedStrings.xml');
  if (xmlTextos) {
    const d = dom.parseFromString(xmlTextos, 'application/xml');
    for (const si of d.getElementsByTagName('si')) {
      // Un texto con varios formatos viene partido en trozos <t>
      let t = '';
      for (const nodo of si.getElementsByTagName('t')) t += nodo.textContent;
      compartidas.push(t);
    }
  }

  // La primera hoja. No siempre se llama sheet1.xml.
  let hoja = null;
  for (const nombre of zip.entradas.keys()) {
    if (/^xl\/worksheets\/sheet\d+\.xml$/.test(nombre)) {
      if (!hoja || nombre < hoja) hoja = nombre;
    }
  }
  if (!hoja) throw new Error('el archivo no tiene ninguna hoja');

  const d = dom.parseFromString(await sacarTexto(zip, hoja), 'application/xml');
  const filas = [];
  for (const row of d.getElementsByTagName('row')) {
    const fila = [];
    for (const c of row.getElementsByTagName('c')) {
      const donde = columnaDeCelda(c.getAttribute('r'));
      const tipo = c.getAttribute('t');
      let valor = '';
      if (tipo === 's') {
        const i = Number(c.getElementsByTagName('v')[0]?.textContent);
        valor = compartidas[i] ?? '';
      } else if (tipo === 'inlineStr') {
        for (const nodo of c.getElementsByTagName('t')) valor += nodo.textContent;
      } else {
        valor = c.getElementsByTagName('v')[0]?.textContent ?? '';
      }
      if (donde >= 0) {
        while (fila.length < donde) fila.push('');
        fila[donde] = valor;
      } else fila.push(valor);
    }
    filas.push(fila);
  }
  return filas.filter(f => f.some(x => String(x).trim() !== ''));
}

// ── Pegar directamente desde Excel ──────────────────────────────────────────
//
// Copiar celdas en Excel y pegarlas pone el texto separado por tabulaciones.
// Es el camino más corto de todos: ni guardar como CSV, ni buscar el archivo.
export function leerPegado(texto) {
  return leerCSV(String(texto || '').replace(/^﻿/, ''));
}
